// eslint-disable-next-line @github-ui/github-monorepo/filename-convention
import {gsap} from 'gsap'
import {CustomEase} from 'gsap/dist/CustomEase'
import {useEffect, useEffectEvent, useLayoutEffect, useRef, useState} from 'react'

import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'

import Styles from './DataMarquee.module.css'

export function useDataMarqueeMotion<Item>(items: Item[]) {
  const viewportRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLUListElement>(null)
  const syncRef = useRef<(() => void) | null>(null)
  const scrollAdjustmentRef = useRef(0)
  const animatedScrollRef = useRef(0)
  const prefersReducedMotion = usePrefersReducedMotion()
  const [playback, setPlayback] = useState({prefersReducedMotion, isPaused: prefersReducedMotion})
  const [firstItem, setFirstItem] = useState(0)
  const [hasOverflow, setHasOverflow] = useState(false)
  const isPaused = playback.isPaused || (prefersReducedMotion && !playback.prefersReducedMotion)
  const isPlaybackPaused = useEffectEvent(() => isPaused)

  if (playback.prefersReducedMotion !== prefersReducedMotion) {
    setPlayback({prefersReducedMotion, isPaused})
  }

  const offset = items.length > 0 ? firstItem % items.length : 0
  const orderedItems = [...items.slice(offset), ...items.slice(0, offset)].map((item, index) => ({
    item,
    key: firstItem + index,
  }))
  const first = orderedItems[0]
  if (hasOverflow && first) {
    orderedItems.push({item: first.item, key: firstItem + items.length})
  }

  useLayoutEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return

    // React recycles the outgoing cards; compensate before paint to keep the next card stationary.
    const scrollLeft = Math.max(0, viewport.scrollLeft - scrollAdjustmentRef.current)
    viewport.scrollTo({left: scrollLeft, behavior: 'instant'})
    scrollAdjustmentRef.current = 0
    animatedScrollRef.current = scrollLeft
  }, [firstItem])

  useEffect(() => {
    const viewport = viewportRef.current
    const track = trackRef.current
    const container = viewport?.parentElement
    if (!viewport || !track || !container || items.length === 0) return

    gsap.registerPlugin(CustomEase)
    const ease = CustomEase.create('dataMarquee', '0.22,1,0.36,1')
    let isInView = false
    let isHovered = viewport.matches(':hover')
    let isFocused = viewport.contains(document.activeElement)
    let timeline: gsap.core.Timeline | undefined
    const context = gsap.context(() => {}, viewport)

    const updateScrollPosition = () => {
      container.classList.toggle(Styles['DataMarquee--scrolled'], firstItem > 0 || viewport.scrollLeft > 0)
    }

    const sync = () => {
      timeline?.paused(isPlaybackPaused() || isHovered || isFocused || !isInView || document.hidden)
    }
    const measure = () => {
      const cards = Array.from(track.children).filter((child): child is HTMLLIElement => child instanceof HTMLLIElement)
      const firstCard = cards[0]
      if (!firstCard) return

      // Read all card positions together, before creating or updating the animation.
      // eslint-disable-next-line @github-ui/web-vitals/no-layout-read-in-loop
      const offsets = cards.map(card => card.offsetLeft - firstCard.offsetLeft)
      const lastOriginalCard = cards[items.length - 1]
      const contentWidth = lastOriginalCard
        ? lastOriginalCard.offsetLeft + lastOriginalCard.offsetWidth - firstCard.offsetLeft
        : 0
      const scrollLeft = viewport.scrollLeft
      const maxScroll = viewport.scrollWidth - viewport.clientWidth
      const overflows = items.length > 1 && contentWidth > viewport.clientWidth

      setHasOverflow(overflows)
      timeline?.kill()
      if (!overflows || !hasOverflow) return

      const nextIndex = offsets.findIndex(position => position > scrollLeft + 1)
      const distance = offsets[nextIndex]
      if (distance === undefined) return
      const previousDistance = offsets[nextIndex - 1]
      if (distance > maxScroll && nextIndex > 1 && previousDistance !== undefined) {
        scrollAdjustmentRef.current = previousDistance
        setFirstItem(index => index + nextIndex - 1)
        return
      }
      animatedScrollRef.current = scrollLeft

      context.add(() => {
        timeline = gsap.timeline({
          paused: true,
          onComplete: () => {
            scrollAdjustmentRef.current = distance
            setFirstItem(index => index + nextIndex)
          },
        })
        timeline.to(
          viewport,
          {
            scrollLeft: distance,
            duration: prefersReducedMotion ? 0 : 0.9,
            ease,
            onUpdate: () => {
              animatedScrollRef.current = viewport.scrollLeft
              updateScrollPosition()
            },
          },
          firstItem === 0 ? 0.5 : prefersReducedMotion ? 1.5 : 0.6,
        )
      })
      sync()
    }
    syncRef.current = () => {
      if (!isPlaybackPaused() && viewport.scrollLeft !== animatedScrollRef.current) measure()
      else sync()
    }

    const onPointerEnter = (event: PointerEvent) => {
      if (event.pointerType === 'touch') return
      isHovered = true
      sync()
    }
    const onPointerLeave = () => {
      isHovered = false
      if (viewport.scrollLeft !== animatedScrollRef.current) measure()
      sync()
    }
    const onFocusIn = () => {
      isFocused = true
      sync()
    }
    const onFocusOut = (event: FocusEvent) => {
      isFocused = event.relatedTarget instanceof Node && viewport.contains(event.relatedTarget)
      if (!isFocused && viewport.scrollLeft !== animatedScrollRef.current) measure()
      sync()
    }
    const onVisibilityChange = () => sync()
    const intersectionObserver = new IntersectionObserver(([entry]) => {
      isInView = entry?.isIntersecting ?? false
      sync()
    })
    const resizeObserver = new ResizeObserver(measure)
    intersectionObserver.observe(viewport)
    resizeObserver.observe(viewport)
    resizeObserver.observe(track)
    viewport.addEventListener('pointerenter', onPointerEnter)
    viewport.addEventListener('pointerleave', onPointerLeave)
    viewport.addEventListener('pointercancel', onPointerLeave)
    viewport.addEventListener('focusin', onFocusIn)
    viewport.addEventListener('focusout', onFocusOut)
    // eslint-disable-next-line github/prefer-observers -- Keep the fade in sync when keyboard focus reveals a card.
    viewport.addEventListener('scroll', updateScrollPosition)
    document.addEventListener('visibilitychange', onVisibilityChange)
    updateScrollPosition()
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-external-store-subscription -- Measured overflow determines whether React renders the incoming card.
    measure()

    return () => {
      intersectionObserver.disconnect()
      resizeObserver.disconnect()
      viewport.removeEventListener('pointerenter', onPointerEnter)
      viewport.removeEventListener('pointerleave', onPointerLeave)
      viewport.removeEventListener('pointercancel', onPointerLeave)
      viewport.removeEventListener('focusin', onFocusIn)
      viewport.removeEventListener('focusout', onFocusOut)
      viewport.removeEventListener('scroll', updateScrollPosition)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      // Leave native scroll position intact when React advances the list.
      context.kill()
      syncRef.current = null
    }
  }, [items, firstItem, hasOverflow, prefersReducedMotion])

  useEffect(() => {
    syncRef.current?.()
  }, [isPaused])

  const togglePlayback = () => setPlayback({prefersReducedMotion, isPaused: !isPaused})

  return {viewportRef, trackRef, orderedItems, isPaused, togglePlayback}
}
